import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import DoctorAvailability from './DoctorAvailability';

const COLORS = {
  primary: "#0760ad",
  secondary: "#118a94",
  white: "#FFFFFF"
};

const AppointmentForm = ({ doctor, onClose }) => {
  const [formData, setFormData] = useState({
    patient_name: '',
    patient_email: '',
    patient_phone: '',
    appointment_date: '',
    appointment_time: '',
    reason: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const apiUrl = import.meta.env.VITE_API_URL;

  useEffect(() => {
    if (formData.appointment_date && doctor) {
      fetchAvailableSlots(formData.appointment_date);
    }
  }, [formData.appointment_date, doctor]);

  const fetchAvailableSlots = async (date) => {
    if (!doctor) return;

    setLoadingSlots(true);
    try {
      const response = await axios.post(`${apiUrl}/doctor-availability/`, {
        doctor_id: doctor.id,
        date: date
      });
      setAvailableSlots(response.data.available_slots || []);
    } catch (error) {
      console.error('Error fetching available slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'appointment_date' ? { appointment_time: '' } : {})
    }));
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.patient_name.trim()) errors.patient_name = 'Name is required';
    if (!formData.patient_email.trim()) errors.patient_email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.patient_email)) 
      errors.patient_email = 'Invalid email format';
    if (!formData.patient_phone.trim()) errors.patient_phone = 'Phone is required';
    if (!formData.appointment_date) errors.appointment_date = 'Date is required';
    if (!formData.appointment_time) errors.appointment_time = 'Time is required';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const formatTimeTo24Hour = (time) => {
    const [timePart, modifier] = time.split(' ');
    const [hours, minutes] = timePart.split(':');
  
    let formattedHour = parseInt(hours, 10);
    if (modifier === 'PM' && formattedHour < 12) {
      formattedHour += 12; // Convert PM times to 24-hour format
    } else if (modifier === 'AM' && formattedHour === 12) {
      formattedHour = 0; // Convert 12 AM to 00:xx
    }
  
    // Ensure hours and minutes are two digits
    return `${String(formattedHour).padStart(2, '0')}:${minutes}`;
  };
  

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    const formattedTime = formatTimeTo24Hour(formData.appointment_time);

    const appointmentData = {
      doctor: doctor.id,
      patient_name: formData.patient_name,
      patient_email: formData.patient_email,
      patient_phone: formData.patient_phone,
      appointment_date: formData.appointment_date,
      appointment_time: formattedTime,
      reason: formData.reason
    };

    console.log("Submitting appointment data:", appointmentData);

    try {
      const response = await axios.post(`${apiUrl}/appointments/`, appointmentData, {
        headers: {
          'Content-Type': 'application/json',
          // Add authorization header if needed
          // 'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 201) {
        setFormData({
          patient_name: '',
          patient_email: '',
          patient_phone: '',
          appointment_date: '',
          appointment_time: '',
          reason: ''
        });
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          onClose();
        }, 1000);
      }
    } catch (error) {
      console.error('Appointment creation error:', error.response?.data || error.message);
      
      // Handle backend validation errors
      if (error.response?.data) {
        setFormErrors(error.response.data);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      {/* Success Notification */}
      {showSuccess && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed top-4 right-4 z-50"
        >
          <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg flex items-center">
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <span>Appointment booked successfully!</span>
          </div>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-2xl font-bold" style={{ color: COLORS.primary }}>
              Book Appointment
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
              disabled={submitting}
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {doctor && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-4">
                  <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                    {doctor.image_url ? (
                      <img src={doctor.image_url} alt={doctor.name} className="h-full w-full object-cover" />
                    ) : (
                      <span className="text-2xl text-gray-500">
                        {doctor.gender === 'Female' ? '👩‍⚕️' : '👨‍⚕️'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg" style={{ color: COLORS.primary }}>
                    {doctor.name}
                  </h3>
                  <p className="text-sm font-medium" style={{ color: COLORS.secondary }}>
                    {Array.isArray(doctor.specialty) ? doctor.specialty.map(s => s.name).join(', ') : ''}
                  </p>
                  
                  <div className="mt-2 text-sm text-gray-600">
                    <DoctorAvailability
                      availability={doctor.availability} 
                      workingHours={doctor.working_hours} 
                      compact={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="patient_name"
                  value={formData.patient_name}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border ${formErrors.patient_name ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-2 focus:ring-[#0760ad] focus:border-[#0760ad] transition`}
                />
                {formErrors.patient_name && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.patient_name}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="patient_email"
                  value={formData.patient_email}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border ${formErrors.patient_email ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-2 focus:ring-[#0760ad] focus:border-[#0760ad] transition`}
                />
                {formErrors.patient_email && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.patient_email}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="patient_phone"
                  value={formData.patient_phone}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border ${formErrors.patient_phone ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-2 focus:ring-[#0760ad] focus:border-[#0760ad] transition`}
                />
                {formErrors.patient_phone && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.patient_phone}</p>
                )}
              </div>

              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                  Preferred Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="date"
                  name="appointment_date"
                  value={formData.appointment_date}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  className={`w-full px-4 py-2 border ${formErrors.appointment_date ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-2 focus:ring-[#0760ad] focus:border-[#0760ad] transition`}
                />
                {formErrors.appointment_date && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.appointment_date}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1">
                Preferred Time <span className="text-red-500">*</span>
              </label>
              <select
                id="time"
                name="appointment_time"
                value={formData.appointment_time}
                onChange={handleChange}
                className={`w-full px-4 py-2 border ${formErrors.appointment_time ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-2 focus:ring-[#0760ad] focus:border-[#0760ad] transition`}
                disabled={!formData.appointment_date || loadingSlots}
              >
                <option value="">Select time</option>
                {loadingSlots ? (
                  <option value="" disabled>Loading available times...</option>
                ) : availableSlots.length > 0 ? (
                  availableSlots.map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))
                ) : formData.appointment_date ? (
                  <option value="" disabled>No available slots for this date</option>
                ) : null}
              </select>
              {formErrors.appointment_time && (
                <p className="mt-1 text-sm text-red-600">{formErrors.appointment_time}</p>
              )}
              {formData.appointment_date && availableSlots.length === 0 && !loadingSlots && (
                <p className="mt-1 text-sm text-red-600">Doctor is not available on this date</p>
              )}
            </div>

            <div>
              <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Visit
              </label>
              <textarea
                id="reason"
                name="reason"
                rows={3}
                value={formData.reason}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0760ad] focus:border-[#0760ad] transition"
                placeholder="Please describe the reason for your visit"
              ></textarea>
            </div>

            <div className="pt-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-md font-medium text-gray-700 hover:bg-gray-50 transition"
                disabled={submitting}
              >
                Cancel
              </button>
              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-6 py-2 rounded-md font-medium text-white"
                style={{ backgroundColor: COLORS.primary }}
                disabled={submitting || !formData.appointment_time}
              >
                {submitting ? 'Processing...' : 'Confirm Appointment'}
              </motion.button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default AppointmentForm;